import type { loadVarianceNotes } from "../lib/ai/varianceNotesStore";
import type { ClaimBasicsSnapshot, AnnotatedPdfAttachment, IncurredReceiptAttachment } from "../lib/claimSession";
import type { SupplementItem } from "../lib/lineItemCompare";
import type { PacketPhoto } from "../lib/packet/packetStore";
import type { PdfHighlight } from "../lib/pdf/types";
import type { Estimate } from "../types/items";
import type { Step } from "../workflow/steps";
import { apiUrl } from "../lib/apiBase";

export type VarianceNotesSnapshot = ReturnType<typeof loadVarianceNotes>;

export type ProgressSnapshot = {
  reportKey?: string;
  claimBasics?: ClaimBasicsSnapshot | null;
  carrierSessionId?: string | null;
  insuredSessionId?: string | null;
  carrierParsed?: unknown;
  insuredParsed?: unknown;
  leftEstimate?: Estimate | null;
  rightEstimate?: Estimate | null;
  supplementItems?: SupplementItem[];
  packetNotes?: Record<string, string>;
  packetPhotos?: PacketPhoto[];
  annotatedPdfAttachments?: AnnotatedPdfAttachment[];
  incurredReceipts?: IncurredReceiptAttachment[];
  pdfHighlights?: PdfHighlight[];
  comparisonSelections?: Record<string, string[]>;
  activeComparisonPairId?: string | null;
  lastRecapVariancePath?: string | null;
  varianceNotes?: VarianceNotesSnapshot;
};

export type ProgressRecord = {
  id: string;
  claimNumber?: string;
  insuredName?: string;
  carrierName?: string;
  lossAddress?: string;
  itemCount?: number;
  total?: number;
  step?: Step;
  route?: string;
  createdAt: number;
  updatedAt: number;
  snapshot: ProgressSnapshot;
};

const PROGRESS_KEY = "claimsupp:progress:v1";

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function makeProgressId(seed?: string) {
  const base = String(seed || "").trim();
  if (base) return base;
  const rand = Math.random().toString(16).slice(2);
  return `progress_${Date.now()}_${rand}`;
}

function loadLocalProgress(): ProgressRecord[] {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ProgressRecord[]) : [];
  } catch {
    return [];
  }
}

export async function loadProgress(): Promise<ProgressRecord[]> {
  try {
    const res = await fetch(apiUrl("/progress"), { credentials: "include" });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "progress_load_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveProgress(list);
    return list;
  } catch {
    return loadLocalProgress();
  }
}

export function saveProgress(list: ProgressRecord[]): void {
  if (!canUseStorage()) return;
  window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(list));
}

export async function upsertProgress(record: ProgressRecord): Promise<ProgressRecord[]> {
  try {
    const res = await fetch(apiUrl("/progress"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ record }),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "progress_save_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveProgress(list);
    return list;
  } catch {
    const list = loadLocalProgress();
    const idx = list.findIndex((item) => item.id === record.id);
    if (idx >= 0) {
      list[idx] = {
        ...list[idx],
        ...record,
        createdAt: list[idx].createdAt || record.createdAt,
        updatedAt: record.updatedAt,
      };
    } else {
      list.unshift(record);
    }
    saveProgress(list);
    return list;
  }
}

export function createProgressRecord(input: {
  id?: string;
  claimNumber?: string;
  insuredName?: string;
  carrierName?: string;
  lossAddress?: string;
  itemCount?: number;
  total?: number;
  step?: Step;
  route?: string;
  snapshot: ProgressSnapshot;
}): ProgressRecord {
  const now = Date.now();
  return {
    id: makeProgressId(input.id),
    claimNumber: input.claimNumber,
    insuredName: input.insuredName,
    carrierName: input.carrierName,
    lossAddress: input.lossAddress,
    itemCount: input.itemCount,
    total: input.total,
    step: input.step,
    route: input.route,
    createdAt: now,
    updatedAt: now,
    snapshot: input.snapshot,
  };
}

export async function removeProgress(id: string): Promise<ProgressRecord[]> {
  try {
    const res = await fetch(apiUrl(`/progress/${encodeURIComponent(id)}`), {
      method: "DELETE",
      credentials: "include",
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "progress_delete_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveProgress(list);
    return list;
  } catch {
    const list = loadLocalProgress().filter((item) => item.id !== id);
    saveProgress(list);
    return list;
  }
}

export async function clearProgress(): Promise<ProgressRecord[]> {
  try {
    const res = await fetch(apiUrl("/progress"), {
      method: "DELETE",
      credentials: "include",
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "progress_clear_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveProgress(list);
    return list;
  } catch {
    saveProgress([]);
    return [];
  }
}
